import React from "react";
import { FaTrash } from "react-icons/fa";
import { GooglePlacesInput } from "../../components/googlePlacesInput/GooglePlacesInput";
import { useGoogleMapsScript } from "../../helper/useGoogleMapsScript";
import "../tutorRegistration/TutorRegistration.css";
import { useUserLocation } from "../../providers/LocationProvider";

const Step4 = ({
  errors,
  formData,
  handleInputChange,
  handleCourseSelect,
  handleRemoveCourse,
  handleLocationChange,
}) => {
  const { setLocation } = useUserLocation();

  const { ready } = useGoogleMapsScript();

  const coursesOptions = [
    "Mathematics",
    "English",
    "Physics",
    "Chemistry",
    "Biology",
    "Computer Science",
    "Economics",
    "Accountancy",
    "Government",
    "Literature",
    "Further Mathematics",
    "Geography",
    "History",
  ];

  const currencyOptions = [
    { value: "NGN", label: "NGN (₦)", symbol: "₦" },
    { value: "USD", label: "USD ($)", symbol: "$" },
    { value: "EUR", label: "EUR (€)", symbol: "€" },
    { value: "GBP", label: "GBP (£)", symbol: "£" },
  ];

  const programTypeOptions = [
    { value: "online", label: "Online" },
    { value: "physical", label: "Physical" },
    { value: "both", label: "Both" },
  ];

  const showLocationField =
    formData.programType === "physical" || formData.programType === "both";

  const renderProgramType = () => (
    <div className="form-group">
      <label>Program Type *</label>
      <select
        name="programType"
        value={formData.programType || ""}
        onChange={handleInputChange}
        className="tutor-input-field"
      >
        <option value="">Select Program Type</option>
        {programTypeOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {errors.programType && <p className="error">{errors.programType}</p>}
      <p className="tooltip">
        Tooltips: Choose how you prefer to conduct tutoring sessions. Physical
        sessions require a location.
      </p>
    </div>
  );

  const renderCourses = () => (
    <div className="form-group">
      <label>Courses *</label>
      <select
        name="courses"
        value=""
        onChange={handleCourseSelect}
        className="tutor-input-field"
        disabled={formData.selectedCourses.length >= 5}
      >
        <option value="">Select a course</option>
        {coursesOptions.map((course) => (
          <option key={course} value={course}>
            {course}
          </option>
        ))}
      </select>
      {errors.courses && <p className="error">{errors.courses}</p>}
      <p className="tooltip">
        Tooltips: Select up to 5 courses you specialize in. You can add more
        later if needed.
      </p>
      {formData.selectedCourses.length > 0 && (
        <div className="selected-courses">
          <p className="selected-courses-label">Selected Courses:</p>
          {formData.selectedCourses.map((course, index) => (
            <div key={index} className="selected-course-item">
              <span>{course}</span>
              <button
                type="button"
                onClick={() => handleRemoveCourse(course)}
                className="remove-course-btn"
                disabled={formData.selectedCourses.length <= 1}
              >
                <FaTrash />
              </button>
            </div>
          ))}
          {formData.selectedCourses.length >= 5 && (
            <p className="max-courses-warning">Maximum 5 courses reached</p>
          )}
        </div>
      )}
    </div>
  );

  const renderHourlyRate = () => (
    <div className="form-group">
      <label>Hourly Rate *</label>
      <div className="url-input-container">
        <select
          name="currency"
          value={formData.currency || "NGN"}
          onChange={handleInputChange}
          className="url-prefix currency-select"
        >
          {currencyOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="number"
          name="hourlyRate"
          placeholder="Enter hourly rate"
          value={formData.hourlyRate || ""}
          onChange={handleInputChange}
          className="url-input"
          min="0"
          step="0.01"
        />
      </div>
      {errors.hourlyRate && <p className="error">{errors.hourlyRate}</p>}
      <p className="tooltip">
        Tooltips: Set your hourly rate in your preferred currency. This can be
        adjusted later.
      </p>
    </div>
  );

  const renderLocationField1 = () => (
    <div className="form-group">
      <label>Teaching Address/Location *</label>
      <input
        type="text"
        name="location"
        placeholder="Enter your full address (street, city, state)"
        value={formData.location || ""}
        onChange={handleLocationChange}
        className="tutor-input-field"
        required
      />
      {errors.location && <p className="error">{errors.location}</p>}
      <div className="tooltip">
        <small>Example: 123 Main Street, Ikeja, Lagos State</small>
      </div>
    </div>
  );

  const renderLocationField = () => (
    <div className="form-group">
      <label>Teaching Address/Location *</label>

      {ready ? (
        <GooglePlacesInput
          value={formData.location?.address || ""}
          placeholder="Enter your full address (street, city, state)"
          onPlaceSelect={(place) => {
            const formatted = {
              address: place.formatted_address,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
              placeId: place.place_id,
            };

            handleLocationChange({
              target: { name: "location", value: formatted },
            });

            setLocation({
              lat: formatted.lat,
              lng: formatted.lng,
            }); // 👈 ADD HERE
          }}
        />
      ) : (
        <input
          // type="text"
          // className="tutor-input-field"
          placeholder="Loading Google Places..."
          disabled
        />
      )}

      {errors.location && <p className="error">{errors.location}</p>}
      <div className="tooltip">
        <small>Example: 123 Main Street, Ikeja, Lagos State</small>
      </div>
    </div>
  );

  return (
    <>
      <div className="form-title">
        <h2>Add Courses and Program Type</h2>
        <p className="subtitle">
          Add courses you specialize in and willing to teach students.
        </p>
      </div>

      {renderProgramType()}
      {renderCourses()}
      {renderHourlyRate()}

      {showLocationField && renderLocationField()}

      {formData.programType === "online" && formData.location && (
        <div className="form-group">
          <p className="warning">
            Location will not be required for online sessions. You can clear it
            or keep it for future physical session options.
          </p>
        </div>
      )}
    </>
  );
};

export default Step4;
